<template>
  <nav v-if="crumbs.length > 1" class="smart-breadcrumbs" aria-label="Breadcrumb">
    <ol class="crumb-list">
      <li v-for="(crumb, idx) in crumbs" :key="crumb.path" class="crumb-item">
        <router-link
            v-if="idx < crumbs.length - 1"
            :to="crumb.path"
            class="crumb-link"
        >
          <MdpsIcon v-if="crumb.icon" :name="crumb.icon" size="x-small" class="crumb-icon"/>
          <span>{{ crumb.label }}</span>
        </router-link>
        <span v-else class="crumb-current">{{ crumb.label }}</span>
        <MdpsIcon v-if="idx < crumbs.length - 1" name="angle-right" size="x-small" class="crumb-separator"/>
      </li>
    </ol>
  </nav>
</template>

<script setup lang="ts">
import {computed} from 'vue'
import {useRoute} from 'vue-router'
import MdpsIcon from '@mdps/icon-vue3'

interface Crumb {
  label: string
  path: string
  icon?: string
}

const route = useRoute()

const routeLabels: Record<string, string> = {
  '/': 'Initiatives Hub',
  '/submit': 'Submit Initiative',
  '/initiatives': 'Initiatives',
  '/search': 'Search',
  '/profile': 'Profile',
  '/notifications': 'Notifications',
  '/contact': 'Contact',
  '/admin': 'Admin',
  '/admin/users': 'Users',
  '/admin/organization': 'Organization',
  '/admin/support': 'Support',
  '/reports': 'Reports',
}

const crumbs = computed<Crumb[]>(() => {
  const path = route.path
  const result: Crumb[] = [{label: 'Initiatives Hub', path: '/', icon: 'home'}]

  if (path === '/initiatives') {
    result.push({label: 'Initiatives', path: '/initiatives'})
  } else if (path.startsWith('/initiatives/')) {
    result.push({label: 'Initiatives', path: '/initiatives'})
    if (route.params.id) {
      result.push({label: (route.meta.title as string) || 'Initiative Detail', path})
    }
  } else if (path.startsWith('/admin')) {
    result.push({label: 'Admin', path: '/admin'})
    const sectionLabel = routeLabels[path]
    if (sectionLabel && path !== '/admin') {
      result.push({label: sectionLabel, path})
    }
  } else if (path !== '/') {
    const label = routeLabels[path] || (route.meta.title as string) || ''
    if (label) result.push({label, path})
  }

  return result
})
</script>

<style scoped>
.smart-breadcrumbs {
  padding: 0.6rem 1.5rem;
  background: #ffffff;
  border-bottom: 1px solid #f1f5f9;
}

.crumb-list {
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 0.15rem;
  flex-wrap: wrap;
}

.crumb-item {
  display: flex;
  align-items: center;
  gap: 0.15rem;
}

.crumb-link {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  font-weight: 500;
  color: #64748b;
  text-decoration: none;
  padding: 0.2rem 0.4rem;
  border-radius: 5px;
  transition: color 0.15s, background 0.15s;
}

.crumb-link:hover {
  color: #3b82f6;
  background: #f8fafc;
}

.crumb-icon {
  color: inherit;
}

.crumb-separator {
  color: #cbd5e1;
  margin: 0 0.1rem;
}

.crumb-current {
  font-size: 0.78rem;
  font-weight: 600;
  color: #1e293b;
  padding: 0.2rem 0.4rem;
}

@media (max-width: 640px) {
  .smart-breadcrumbs {
    padding: 0.4rem 1rem;
  }
}
</style>
